import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  TextField,
  TextAreaField,
  CheckboxField,
} from "../../components/ui/FormControls";
import { ImageDropField } from "../../components/ui/ImageDropField";
import Loader from "../../components/Loader";
import {
  createBanner,
  fetchBannerById,
  updateBanner,
  resetBannerStatuses,
} from "../../store/slices/bannersSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";

// Validation schema based on API requirements
const createBannerSchema = yup.object({
  image: yup
    .mixed()
    .test("required", "Banner image is required", (value) => {
      return value && value.length > 0;
    })
    .test("fileSize", "Image size must be less than 10MB", (value) => {
      if (!value) return true;
      return Array.from(value).every((file) => {
        // Skip size check for existing images (they don't have size property)
        if (file.existing) return true;
        return file.size <= 10485760; // 10MB
      });
    })
    .test(
      "fileType",
      "Only JPEG, PNG, JPG, WEBP files are allowed",
      (value) => {
        if (!value) return true;
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/webp",
        ];
        return Array.from(value).every((file) => {
          // Skip type check for existing images
          if (file.existing) return true;
          return allowedTypes.includes(file.type);
        });
      }
    )
    .required("Banner image is required"),
  headline: yup
    .string()
    .max(255, "Headline must be less than 255 characters")
    .nullable(),
  tagline: yup
    .string()
    .max(255, "Tagline must be less than 255 characters")
    .nullable(),
  sequence: yup
    .number()
    .typeError("Sequence must be a number")
    .min(0, "Sequence must be 0 or greater")
    .required("Sequence is required"),
  is_enabled: yup.boolean().default(true),
});

// Edit mode schema - image is optional
const editBannerSchema = yup.object({
  image: yup
    .mixed()
    .test("fileSize", "Image size must be less than 10MB", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every((file) => {
        // Skip size check for existing images (they don't have size property)
        if (file.existing) return true;
        return file.size <= 10485760; // 10MB
      });
    })
    .test(
      "fileType",
      "Only JPEG, PNG, JPG, WEBP files are allowed",
      (value) => {
        if (!value || value.length === 0) return true;
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/webp",
        ];
        return Array.from(value).every((file) => {
          // Skip type check for existing images
          if (file.existing) return true;
          return allowedTypes.includes(file.type);
        });
      }
    )
    .nullable(),
  headline: yup
    .string()
    .max(255, "Headline must be less than 255 characters")
    .nullable(),
  tagline: yup
    .string()
    .max(255, "Tagline must be less than 255 characters")
    .nullable(),
  sequence: yup
    .number()
    .typeError("Sequence must be a number")
    .min(0, "Sequence must be 0 or greater")
    .required("Sequence is required"),
  is_enabled: yup.boolean().default(true),
});

const defaultValues = {
  image: null,
  headline: "",
  tagline: "",
  sequence: "",
  is_enabled: true,
};

const AddBannerPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editBannerId = searchParams.get("edit");
  const isEditMode = Boolean(editBannerId);

  const {
    bannerCreateStatus,
    bannerUpdateStatus,
    banner,
    bannerFetchStatus,
    loading,
  } = useSelector((state) => state.banner);

  const methods = useForm({
    mode: "onSubmit",
    defaultValues,
    resolver: yupResolver(isEditMode ? editBannerSchema : createBannerSchema),
  });

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = methods;

  // Reset statuses on unmount
  useEffect(() => {
    return () => {
      dispatch(resetBannerStatuses());
    };
  }, [dispatch]);

  // Handle create success/failure
  useEffect(() => {
    if (bannerCreateStatus === "success") {
      reset(defaultValues);
      showApiSuccess("Banner added successfully");
      dispatch(resetBannerStatuses());
      navigate("/banners");
    } else if (bannerCreateStatus === "failed") {
      showApiError(null, "Failed to create banner");
    }
  }, [bannerCreateStatus, navigate, reset, dispatch]);

  useEffect(() => {
    if (bannerUpdateStatus === "success") {
      showApiSuccess("Banner updated successfully");
      dispatch(resetBannerStatuses());
      navigate("/banners");
    } else if (bannerUpdateStatus === "failed") {
      showApiError(null, "Failed to update banner");
    }
  }, [bannerUpdateStatus, navigate, dispatch]);

  // Fetch banner data if in edit mode
  useEffect(() => {
    if (isEditMode && editBannerId) {
      dispatch(fetchBannerById(editBannerId));
    }
  }, [dispatch, isEditMode, editBannerId]);

  // Populate form with banner data when in edit mode
  useEffect(() => {
    if (isEditMode && banner && bannerFetchStatus === "success") {
      const formData = {
        image: banner.image_url
          ? [
              {
                preview: banner.image_url,
                existing: true,
                id: banner.id,
              },
            ]
          : null,
        headline: banner.headline || "",
        tagline: banner.tagline || "",
        sequence: banner.sequence?.toString() || "",
        is_enabled: Boolean(banner.is_enabled),
      };

      reset(formData);
    }
  }, [isEditMode, banner, bannerFetchStatus, reset]);

  const buildBannerFormData = (bannerData) => {
    const formData = new FormData();

    // Handle image upload
    if (bannerData.image && bannerData.image.length > 0) {
      const imageFile = bannerData.image[0];
      
      // Only append new image files (not existing ones)
      if (imageFile instanceof File) {
        formData.append("image", imageFile);
      } else if (imageFile && typeof imageFile === 'object' && !imageFile.existing) {
        // Handle case where it might be wrapped in an object
        if (imageFile.file) {
          formData.append("image", imageFile.file);
        }
      }
      // Skip existing images - they don't need to be sent in edit mode
    }

    // Add other fields
    if (bannerData.headline) {
      formData.append("headline", bannerData.headline);
    }
    if (bannerData.tagline) {
      formData.append("tagline", bannerData.tagline);
    }
    if (bannerData.sequence !== "" && bannerData.sequence !== null && bannerData.sequence !== undefined) {
      formData.append("sequence", bannerData.sequence);
    }
    formData.append("is_enabled", bannerData.is_enabled ? 1 : 0);

    return formData;
  };

  const onSubmit = (data) => {
    console.log("Form data before building FormData:", data);
    const formData = buildBannerFormData(data);
    
    // Debug: Log FormData contents
    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    if (isEditMode) {
      dispatch(updateBanner({ uuid: editBannerId, data: formData }));
    } else {
      dispatch(createBanner(formData));
    }
  };

  // Show loader while fetching banner data in edit mode
  if (isEditMode && bannerFetchStatus === "loading") {
    return <Loader />;
  }

  // Show error if banner not found in edit mode
  if (isEditMode && bannerFetchStatus === "failed") {
    return (
      <div className="max-w-2xl mx-auto theme-card rounded-lg p-6 text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">
          Banner Not Found
        </h2>
        <p className="text-gray-600 mb-4">
          The banner you're trying to edit could not be found.
        </p>
        <button
          onClick={() => navigate("/banners")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Back to Banners
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {loading && <Loader />}
      <form
        className="theme-card rounded-lg p-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? "Edit Banner" : "Add New Banner"}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode
              ? "Update banner information"
              : "Fill in the details to add a new banner"}
          </p>
        </div>

        <div className="space-y-6">
          {/* Banner Image */}
          <div>
            <Controller
              control={control}
              name="image"
              render={({ field }) => (
                <ImageDropField
                  label="Banner Image"
                  required={!isEditMode}
                  value={field.value}
                  onChange={(files) => field.onChange(files)}
                  hint={
                    isEditMode
                      ? "Upload new banner image to replace existing (JPEG, PNG, JPG, WEBP - Max 10MB) - Optional"
                      : "Upload banner image (JPEG, PNG, JPG, WEBP - Max 10MB)"
                  }
                  maxFiles={1}
                  error={errors?.image?.message}
                />
              )}
            />
          </div>

          {/* Headline */}
          <div>
            <TextField
              label="Headline"
              placeholder="Enter banner headline (optional)"
              {...register("headline")}
              error={errors?.headline?.message}
            />
          </div>

          {/* Tagline */}
          <div>
            <TextAreaField
              label="Tagline"
              rows={3}
              placeholder="Enter banner tagline (optional)"
              {...register("tagline")}
              error={errors?.tagline?.message}
            />
          </div>

          {/* Sequence */}
          <div>
            <TextField
              label="Sequence"
              type="number"
              min={0}
              required
              placeholder="Enter display sequence"
              {...register("sequence")}
              error={errors?.sequence?.message}
            />
          </div>

          {/* Is Enabled */}
          <div>
            <Controller
              control={control}
              name="is_enabled"
              render={({ field }) => (
                <CheckboxField
                  label="Enable Banner"
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/banners")}
            className="theme-button-secondary px-4 py-2 rounded-md transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              bannerCreateStatus === "loading" ||
              bannerUpdateStatus === "loading"
            }
            className="bg-success text-white px-4 py-2 rounded-md hover:bg-success-600 transition-colors disabled:opacity-50"
          >
            {bannerCreateStatus === "loading" ||
            bannerUpdateStatus === "loading"
              ? "Saving..."
              : isEditMode
              ? "Update Banner"
              : "Save Banner"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBannerPage;
